import { type AnalyticsEvents } from '../types/events'
import { DniAnalytics } from './Analytics'
import { AnalyticsPlatforms, getAnalyticsInstance } from './createAnalyticsProviders'
import { GA4 } from './GA4'
import { type AletheiaEventPayload, type SbjsGlobal, buildAletheiaUtm, consumeAletheiaSessionStart, getMetaPixelClickID, getTrackingQueryParams, sendAletheiaEvent } from './utils'

export interface AletheiaConfig {
  userId?: string
  appEnv: string
  projectCode?: string
  countryCode?: string
  campaignCode?: string
}

export class Aletheia extends DniAnalytics<AnalyticsEvents> {
  readonly config: AletheiaConfig
  private static instance: Aletheia | null = null
  private gaClientId: string | undefined

  constructor(config: AletheiaConfig) {
    super()
    this.config = config
    this.initialize()
  }

  protected async initialize() {
    await this.setGaClientId()
    this.setIsReady(true)
  }

  public static getInstance(config: AletheiaConfig): Aletheia {
    if (!Aletheia.instance) {
      Aletheia.instance = new Aletheia(config)
    }
    return Aletheia.instance
  }

  private async setGaClientId() {
    try {
      const ga4Instance = getAnalyticsInstance(AnalyticsPlatforms.GA4)
      if (ga4Instance instanceof GA4) {
        const gaClientId = await ga4Instance.getGtagValue<string>('client_id')
        this.gaClientId = gaClientId
      }
    }
    catch (error) {
      console.error('[Aletheia] Set GA4 client id failed', error)
    }
  }

  private async getGa4SessionId() {
    try {
      const ga4Instance = getAnalyticsInstance(AnalyticsPlatforms.GA4)
      if (ga4Instance instanceof GA4) {
        const gaSessionId = await ga4Instance.getGtagValue<string>('session_id')
        return gaSessionId
      }
      return undefined
    }
    catch (error) {
      console.error('[Aletheia] Get GA4 session id failed', error)
      return undefined
    }
  }

  protected async sendAnalyticsEvent(customEvent: AnalyticsEvents & { eventId?: string }) {
    const { eventName, eventParams, eventId } = customEvent
    const gaSessionId = await this.getGa4SessionId()
    const { fbc, fbp } = getMetaPixelClickID()
    const queryParams = getTrackingQueryParams(window.location)
    const documentLocation = queryParams ? String(window.location.href) + queryParams : window.location.href
    // window.sbjs 是 WooCommerce sourcebuster 注入的全域，dni-analysis 未宣告該型別，於邊界處 cast
    const sbjs = (window as typeof window & { sbjs?: SbjsGlobal }).sbjs
    const utmFirst = buildAletheiaUtm(sbjs?.get?.first)
    const payload: AletheiaEventPayload = {
      event: {
        app_env: this.config.appEnv,
        event_name: eventName,
        user_id: this.config.userId,
        project_code: this.config.projectCode,
        country_code: this.config.countryCode,
        campaign_code: this.config.campaignCode,
        event_id: eventId,
        ga_client_id: this.gaClientId,
        ...(utmFirst && { utm: utmFirst }),
        fbc,
        fbp,
        event_info: {
          ...eventParams,
          session_id: gaSessionId,
          document_title: document.title,
          document_location: documentLocation,
          ...(eventName === 'page_view' && { is_session_start: consumeAletheiaSessionStart(gaSessionId) }),
        },
      },
    }
    sendAletheiaEvent(this.config.appEnv, eventName, payload)
  }
}
